<template>
  <ValidationObserver rules="required" v-slot="{ handleSubmit }">
    <right-side-bar
      v-if="openModal"
      button-class="bg-dynamicBackBtn text-white"
      @submit="handleSubmit(functionId ? editFunction : saveFunction)"
      submit="Save"
      @close="cancel"
    >
      <template v-slot:title>
        <p class="text-darkPurple">
          {{
            functionId ? "Edit Organisational Unit" : "New Organisational Unit"
          }}
        </p>
      </template>
      <div>
        <div style="height: 100%" v-if="loadingData">
          <loader size="xxs" :loader-image="false" />
        </div>
        <div v-else>
          <div>
            <div class="grid grid-cols-12 gap-7" style="margin-top: 1.125rem">
              <div class="col-span-12">
                <c-text
                  placeholder="--Enter--"
                  variant="w-full"
                  label="Organisational Unit"
                  v-model="functionCredentials.name"
                  :rules="['required']"
                />
              </div>
              <div class="col-span-12">
                <c-text
                  placeholder="--Enter--"
                  variant="w-full"
                  label="Unit Description"
                  v-model="functionCredentials.description"
                  :rules="['required']"
                />
              </div>
              <div class="col-span-12">
                <HierarchySelect
                  v-model="functionCredentials.hierarchyId"
                  :show-error="hierarchyValidationError"
                  @clear-error="hierarchyValidationError = false"
                />
              </div>
              <div class="col-span-12">
                <c-text
                  placeholder="--Enter--"
                  variant="w-full"
                  label="Cost Centre"
                  v-model="functionCredentials.costCenter"
                />
              </div>
              <div class="col-span-12">
                <c-select
                  placeholder="--Select--"
                  :options="
                    functionData.filter((data) => data.id !== functionId)
                  "
                  variant="w-full"
                  label="Supervisory Unit"
                  v-model="functionCredentials.reportingTo"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </right-side-bar>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from "vee-validate";

export default {
  components: {
    ValidationObserver,
    CText: () => import("@scelloo/cloudenly-ui/src/components/text"),
    CSelect: () => import("@scelloo/cloudenly-ui/src/components/select"),
    RightSideBar: () => import("@/components/RightSideBar"),
    HierarchySelect: () => import("./hierarchySelect.vue"),
  },
  props: {
    openModal: {
      type: Boolean,
      default: false,
    },
    functionId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loadingData: false,
      functionCredentials: {
        name: "",
        costCenter: null,
        description: null,
        reportingTo: null,
        hierarchyId: null,
      },
      creatingFunction: false,
      functionData: [],
      hierarchyValidationError: false,
      kpiList: [],
      cursor: "",
      disableBtn: false,
    };
  },
  methods: {
    reset() {
      this.functionCredentials = {
        name: "",
        costCenter: null,
        description: null,
        reportingTo: null,
        hierarchyId: null,
      };
      this.disableBtn = false;
      this.bgButton = "bg-flame";
      this.cursor = "";
      this.hierarchyValidationError = false;
    },
    cancel() {
      if (this.functionId) {
        this.$emit("closeFunctionModal");
      } else {
        this.reset();
        this.$emit("closeFunctionModal");
      }
    },
    saveFunction() {
      if (!this.functionCredentials.hierarchyId) {
        this.hierarchyValidationError = true;
        this.showHierarchyManager = true;
        return;
      }
      this.creatingFunction = true;

      const reportingTo = this.normalizeReportingTo(
        this.functionCredentials.reportingTo
      );
      this.functionCredentials.orgId = this.$orgId;
      this.functionCredentials.createdBy = this.$AuthUser.id;
      this.functionCredentials.reportingTo = reportingTo;

      this.$_createFunction(this.functionCredentials)
        .then(() => {
          this.$emit("closeFunctionModal");
          this.$toasted.success("Function updated sucessfully", {
            duration: 5000,
          });
          this.reset();
          this.creatingFunction = false;
        })
        .catch((error) => {
          this.creatingFunction = false;
          this.$toasted.error(error, {
            duration: 5000,
          });
          this.reset();
          throw new Error(error);
        });
    },
    editFunction() {
      if (!this.functionCredentials.hierarchyId) {
        this.hierarchyValidationError = true;
        this.showHierarchyManager = true;
        return;
      }
      this.creatingFunction = true;
      const editFunction = {
        functionId: this.functionId,
        name: this.functionCredentials.name,
        description: this.functionCredentials.description,
        costCenter: this.functionCredentials.costCenter
          ? this.functionCredentials.costCenter
          : null,
        hierarchyId: this.functionCredentials.hierarchyId,
        kpis: [],
        reportingTo: this.normalizeReportingTo(
          this.functionCredentials.reportingTo
        ),
      };
      this.$_editFunction(editFunction)
        .then(() => {
          this.creatingFunction = false;
          this.$emit("closeFunctionModal");
          this.$toasted.success("Function updated successfully", {
            duration: 5000,
          });
          this.reset();
        })
        .catch((err) => {
          this.creatingFunction = false;
          this.$emit("closeFunctionModal");
          this.$toasted.error(`${err}`, {
            duration: 5000,
          });
          this.reset();
        });
    },
    getFunctionRoute() {
      this.loadingData = true;
      if (this.functionId) {
        this.$_getFunctionId(this.functionId).then((result) => {
          this.functionCredentials = result.data.orgFunction;
          this.kpiList = result.data.orgFunction.kpis;
        });
        this.functionDetails = true;
        this.kpiDetails = true;
      }
      this.getFunction();
    },
    getFunction() {
      Promise.all([this.$_getFunctionKpis("")]).then(([functionResult]) => {
        this.functionData = functionResult.data.functions || [];
        this.loadingData = false;
      });
    },
    normalizeReportingTo(reportingTo) {
      if (!reportingTo || reportingTo === "null") {
        return null;
      }

      if (typeof reportingTo === "object") {
        return reportingTo.id || null;
      }

      return reportingTo;
    },
  },
  mounted() {
    this.getFunctionRoute();
  },
};
</script>

<style scoped>
.buttons {
  width: 126px;
  height: 44px;
  border-radius: 5px;
}
</style>
